import type { CSSProperties } from "react";
import type { AlbumManifest, Crop, ManifestPage } from "../data/manifest";
import { getLayout } from "../data/layouts";
import { cssFilter } from "../data/filters";
import "./PageRenderer.css";

/**
 * Renders one interior page, pixel-faithful to the print master.
 *
 * Geometry: the page is the 210mm TRIM square. Each slot rect is positioned as
 * a fraction of the trim; the page clips bleed (overflow:hidden), so a
 * full-bleed slot ([-3,-3,216,216]) fills edge-to-edge exactly as it prints.
 *
 * Crop: pure fill, reproduced exactly (NOT object-fit). The slot is an
 * overflow:hidden window; the <img> is scaled to 1/crop.w x 1/crop.h of the
 * slot and offset by -crop.x / -crop.y, so only the crop region shows and it
 * fills the slot. This mirrors sharp's extract(crop) -> resize(slot); the
 * render contract guarantees crop pixel-AR == slot AR (±1%), so no distortion.
 */

function slotStyle(
  left: number,
  top: number,
  width: number,
  height: number,
): CSSProperties {
  return {
    left: `${left * 100}%`,
    top: `${top * 100}%`,
    width: `${width * 100}%`,
    height: `${height * 100}%`,
  };
}

function cropStyle(crop: Crop): CSSProperties {
  // Guard against a degenerate crop (should never happen from a valid plan).
  const w = crop.w > 0 ? crop.w : 1;
  const h = crop.h > 0 ? crop.h : 1;
  return {
    width: `${(1 / w) * 100}%`,
    height: `${(1 / h) * 100}%`,
    left: `${(-crop.x / w) * 100}%`,
    top: `${(-crop.y / h) * 100}%`,
  };
}

export interface PageRendererProps {
  page: ManifestPage;
  photos: AlbumManifest["photos"];
  /** true for the current + immediately-neighbouring pages (eager decode). */
  priority?: boolean;
}

export function PageRenderer({ page, photos, priority }: PageRendererProps) {
  const layout = getLayout(page.layout);

  // Unknown layout id or intentional blank page -> background paper only.
  const background = layout?.background ?? "var(--page)";

  if (!layout) {
    // Parity failure should be visible in dev, not a crash. In prod it degrades
    // to a blank sheet rather than a broken page.
    if (import.meta.env?.DEV) {
      // eslint-disable-next-line no-console
      console.warn(`[PageRenderer] unknown layout id "${page.layout}"`);
    }
  }

  const lookFilter = cssFilter(page.look);

  // Fills are keyed by explicit slotIndex (contract), not array position.
  const fillBySlot = new Map(page.slots.map((s) => [s.slotIndex, s]));

  return (
    <div className="pb-page" style={{ background }} aria-hidden={false}>
      {layout && !page.blank
        ? layout.slots.map((slot) => {
            const fill = fillBySlot.get(slot.index);
            if (!fill) return null; // content pages fill every slot; guard anyway
            const photo = photos[fill.photoId];
            const style = slotStyle(
              slot.left,
              slot.top,
              slot.width,
              slot.height,
            );
            return (
              <div className="pb-slot" style={style} key={slot.index}>
                {photo ? (
                  <img
                    className="pb-photo"
                    style={{ ...cropStyle(fill.crop), filter: lookFilter }}
                    src={photo.srcset.page}
                    alt={photo.alt ?? ""}
                    draggable={false}
                    decoding="async"
                    loading={priority ? "eager" : "lazy"}
                    fetchPriority={priority ? "high" : "auto"}
                  />
                ) : (
                  <div className="pb-photo-missing" aria-hidden="true" />
                )}
              </div>
            );
          })
        : null}
    </div>
  );
}
